/**
 * Coalesce offline `module_inspect_edit` ticks into one payload per module.
 * Server logic adds `total_spent` / `quiz_spent` and overwrites scores, so summing
 * the queued deltas is equivalent to replaying each tick.
 */
import { getOperationName } from "./cache";

/**
 * True when a sync job is a module inspect mutation.
 *
 * @param {Object} job Queue record.
 * @returns {boolean}
 */
export const isModuleInspectJob = (job) => job?.kind === "gql" && getOperationName(job.query) === "module_inspect_edit";

/**
 * Flattens queued inspect variables that may be one object or a list.
 *
 * @param {Array<Object|Array|undefined>} inputs Mixed inspect payloads.
 * @returns {Array<Object>}
 */
export const flattenInspectInputs = (inputs) => {
  const out = [];
  (inputs || []).forEach((item) => {
    if (Array.isArray(item)) {
      item.forEach((row) => {
        if (row && typeof row === "object" && !Array.isArray(row)) out.push(row);
      });
      return;
    }
    if (item && typeof item === "object") out.push(item);
  });
  return out;
};

/**
 * Grouping key: one Mongo details row per course + module title.
 *
 * @param {Object} input ModuleInspectEditInput.
 * @returns {string}
 */
export const inspectGroupKey = (input) => `${input?.course_id || ""}::${input?.module_title || ""}`;

/**
 * Merges two inspect payloads the same way the resolver would if they were sent in order.
 *
 * @param {Object} base Earlier payload.
 * @param {Object} next Later payload.
 * @returns {Object}
 */
export const combineInspectInput = (base, next) => {
  const a = base || {};
  const b = next || {};
  return {
    ...a,
    ...b,
    course_id: b.course_id || a.course_id,
    hash: b.hash || a.hash,
    module_title: b.module_title || a.module_title,
    total_spent: Math.round((Number(a.total_spent || 0) + Number(b.total_spent || 0)) * 100) / 100,
    quiz_spent: Math.round((Number(a.quiz_spent || 0) + Number(b.quiz_spent || 0)) * 100) / 100,
    correct_count: b.correct_count != null ? b.correct_count : a.correct_count,
    total_count: b.total_count != null ? b.total_count : a.total_count,
    progress: b.progress != null ? b.progress : a.progress,
  };
};

/**
 * Collapses inspect inputs so each course/module is sent once.
 *
 * @param {Array<Object>} inputs Flat inspect payloads.
 * @returns {Array<Object>}
 */
export const coalesceInspectInputs = (inputs) => {
  const groups = new Map();
  flattenInspectInputs(inputs).forEach((input) => {
    const key = inspectGroupKey(input);
    if (key === "::") return;
    const prev = groups.get(key);
    groups.set(key, prev ? combineInspectInput(prev, input) : { ...input });
  });
  return [...groups.values()];
};
